import React, { useState } from 'react';
import { Star, MessageSquare, ExternalLink, AlertTriangle, CheckCircle, Clock, Send, RefreshCw } from 'lucide-react';
import { authHeaders } from '../supabaseClient';

/**
 * SurveyFeedbackDisplay - Shows customer satisfaction survey results in receipt detail
 * 
 * Add this component inside your receipt detail modal where you show completed orders.
 * It displays the star rating, feedback text, and Google review status.
 * 
 * NOW ALSO: Includes a "Send Survey" button to manually trigger the customer
 * satisfaction survey via SMS from within the receipt view.
 * 
 * Props:
 * - receipt: The receipt object with survey fields
 * - compact: Boolean for smaller display (default: false)
 * - onSurveySent: Optional callback after survey is successfully sent (to refresh data)
 */

const SurveyFeedbackDisplay = ({ receipt, compact = false, onSurveySent }) => {
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // 'success' | 'error' | null

  // Extract survey data from receipt
  const score = receipt?.customer_satisfaction_score;
  const feedbackText = receipt?.customer_feedback_text;
  const surveyStatus = receipt?.survey_status;
  const googleReviewRequested = receipt?.google_review_requested;
  const surveyCompletedAt = receipt?.survey_completed_at;
  const deliveryStatus = receipt?.delivery_status || receipt?.deliveryStatus;

  // Only show for delivered orders
  if (deliveryStatus !== 'delivered') {
    return null;
  }

  // Determine if survey can be manually sent
  // Allow sending when: no score yet AND survey not completed
  // Allow RE-sending when: survey was sent (link_sent/sent) but not completed and no score
  const canSendSurvey = !score && surveyStatus !== 'completed';
  const hasSurveyBeenSent = surveyStatus === 'sent' || surveyStatus === 'link_sent' || surveyStatus === 'in_progress';

  // Handle manual survey send
  const handleSendSurvey = async () => {
    if (!receipt?.id) return;
    
    setIsSending(true);
    setSendResult(null);

    try {
      const response = await fetch('/api/chat-survey', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          receipt_id: receipt.id,
          conversation_stage: 'start_survey',
          // Manual send/resend from the receipt modal is intentional —
          // bypass the duplicate-survey check in chat-survey.py
          force: true
        })
      });

      const data = await response.json();

      if (data.success) {
        setSendResult('success');
        // Call parent callback to refresh receipt data if provided
        if (onSurveySent) {
          onSurveySent(receipt.id);
        }
        // Auto-clear success message after 4 seconds
        setTimeout(() => setSendResult(null), 4000);
      } else {
        console.error('Survey send failed:', data.error);
        setSendResult('error');
        setTimeout(() => setSendResult(null), 4000);
      }
    } catch (error) {
      console.error('Error sending survey:', error);
      setSendResult('error');
      setTimeout(() => setSendResult(null), 4000);
    } finally {
      setIsSending(false);
    }
  };

  // Show pending state if survey not completed yet (with send button)
  if (!score && surveyStatus !== 'completed') {
    return (
      <div className={`${compact ? 'p-3' : 'p-4'} bg-amber-50 border border-amber-200 rounded-xl`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-700">
            <Clock className={`${compact ? 'w-4 h-4' : 'w-5 h-5'}`} />
            <span className={`font-medium ${compact ? 'text-sm' : ''}`}>
              Survey {hasSurveyBeenSent ? 'Sent - Awaiting Response' : 'Pending'}
            </span>
          </div>
        </div>

        {!compact && (
          <p className="text-xs text-amber-600 mt-1">
            {hasSurveyBeenSent 
              ? 'Survey link was sent to customer via SMS' 
              : 'Customer has not yet received a satisfaction survey'
            }
          </p>
        )}

        {/* Send / Resend Survey Button */}
        {!compact && (
          <div className="mt-3">
            {/* Success feedback */}
            {sendResult === 'success' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg mb-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-medium text-emerald-700">Survey sent successfully!</span>
              </div>
            )}

            {/* Error feedback */}
            {sendResult === 'error' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg mb-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="text-xs font-medium text-red-700">Failed to send survey. Try again.</span>
              </div>
            )}

            <button
              onClick={handleSendSurvey}
              disabled={isSending}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                isSending
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : hasSurveyBeenSent
                    ? 'bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isSending ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>{hasSurveyBeenSent ? 'Resend Survey' : 'Send Customer Survey'}</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  }

  // No survey data at all
  if (!score) {
    return null;
  }

  // Determine rating type
  const isPositive = score >= 4;
  const isNegative = score <= 3;

  // Star display
  const renderStars = () => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} ${
              star <= score 
                ? 'text-amber-400 fill-current' 
                : 'text-slate-300'
            }`}
          />
        ))}
        <span className={`ml-2 font-bold ${compact ? 'text-sm' : 'text-lg'} ${
          isPositive ? 'text-emerald-600' : isNegative ? 'text-red-600' : 'text-amber-600'
        }`}>
          {score}/5
        </span>
      </div>
    );
  };

  // Compact version for list view
  if (compact) {
    return (
      <div className={`flex items-center gap-3 p-2 rounded-lg ${
        isPositive 
          ? 'bg-emerald-50 border border-emerald-200' 
          : isNegative 
            ? 'bg-red-50 border border-red-200'
            : 'bg-amber-50 border border-amber-200'
      }`}>
        {renderStars()}
        {googleReviewRequested && (
          <span className="px-2 py-0.5 bg-blue-100 border border-blue-200 rounded text-[10px] text-blue-700 font-semibold">
            GOOGLE REVIEW
          </span>
        )}
        {isNegative && feedbackText && (
          <AlertTriangle className="w-4 h-4 text-red-500" />
        )}
      </div>
    );
  }

  // Full version for receipt detail modal
  return (
    <div className={`rounded-xl border overflow-hidden ${
      isPositive 
        ? 'bg-emerald-50 border-emerald-200' 
        : isNegative 
          ? 'bg-red-50 border-red-200'
          : 'bg-amber-50 border-amber-200'
    }`}>
      {/* Header */}
      <div className={`p-4 border-b ${
        isPositive 
          ? 'border-emerald-200' 
          : isNegative 
            ? 'border-red-200'
            : 'border-amber-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isPositive ? (
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            ) : isNegative ? (
              <AlertTriangle className="w-5 h-5 text-red-600" />
            ) : (
              <MessageSquare className="w-5 h-5 text-amber-600" />
            )}
            <h4 className="font-semibold text-slate-900">Customer Satisfaction</h4>
          </div>
          {surveyCompletedAt && (
            <span className="text-xs text-slate-500">
              {new Date(surveyCompletedAt).toLocaleDateString()}
            </span>
          )}
        </div>
        
        {/* Stars */}
        <div className="mt-3">
          {renderStars()}
        </div>
      </div>

      {/* Feedback Text (if provided) */}
      {feedbackText && (
        <div className={`p-4 border-b ${
          isPositive 
            ? 'border-emerald-200 bg-white/50' 
            : isNegative 
              ? 'border-red-200 bg-white/50'
              : 'border-amber-200 bg-white/50'
        }`}>
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Customer Feedback:</p>
          <p className={`text-sm ${isNegative ? 'text-red-800' : 'text-slate-700'}`}>
            "{feedbackText}"
          </p>
        </div>
      )}

      {/* Google Review Status */}
      {googleReviewRequested && (
        <div className="p-4 bg-blue-50 border-t border-blue-200">
          <div className="flex items-center gap-2">
            <ExternalLink className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">
              Customer Clicked Google Review Link
            </span>
          </div>
          <p className="text-xs text-blue-600 mt-1">
            Opened the Google review page from the post-rating screen
          </p>
        </div>
      )}

      {/* Action needed for negative feedback */}
      {isNegative && (
        <div className="p-4 bg-red-100 border-t border-red-200">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="text-sm font-semibold text-red-700">Action Recommended</span>
          </div>
          <p className="text-xs text-red-600 mt-1">
            This customer had a less than ideal experience. Consider reaching out to resolve any issues.
          </p>
        </div>
      )}
    </div>
  );
};

export default SurveyFeedbackDisplay;